import { type Registry as Services, service } from '@ember/service';
import Model, { attr, type HasMany, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { ConditionalStrategy } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY,
  COMPETITORS_IMPACTS_STRATEGY_LEVELS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import {
  DYNAMIC_RATE_DISCOUNT_RANGE_ERROR_KIND_PRECEDENCE,
  type DynamicRateDiscountRangeErrorKind,
  getDaysWithPriceRangeError,
  getMultiRoomDaysWithPriceRangeError,
  getRatesWithDiscountRangeError,
} from 'lumen/utils/pricing-assistant/recommendation-settings';
import { getInvalidOffsetRoomCodes } from 'lumen/utils/pricing-assistant/room-hierarchy-settings';

export type ComputedValueCondition = {
  condition_type: 'computed_value';
  name: string;
};

type LeadtimeComparison = { operation: string; value: number };

export type LeadtimeConditionSummary = {
  lowerBound: LeadtimeComparison | null;
  name: 'leadtime';
  upperBound: LeadtimeComparison | null;
};

export type UserEditableCondition = {
  condition_type: 'user_editable_value';
  value: number | null;
};

export type OperandCondition = {
  condition_type: 'operand';
  first: OperandCondition | ComputedValueCondition | UserEditableCondition;
  operation: string;
  second: OperandCondition | ComputedValueCondition | UserEditableCondition;
};

export type StrategyCondition =
  | OperandCondition
  | ComputedValueCondition
  | UserEditableCondition;

export type PriceRangeSettings = {
  day_of_week: {
    [key: number]: {
      maximum: number | null;
      minimum: number | null;
    };
  };
  overrides: {
    [dayOfWeek: number]: {
      [roomId: string]: {
        maximum: number | null;
        minimum: number | null;
      };
    };
  };
  settings_type: 'price_range';
};

export type MinimumPriceDifferenceSettings = {
  day_of_week: {
    [key: number]: number | null;
  };
  enabled: boolean;
  settings_type: 'minimum_difference';
};

export type AllowedRecommendationTypeSettings = {
  allowed: 'all' | 'increase' | 'decrease';
  settings_type: 'allowed_recommendation_type';
};

export type RoundingSettings = {
  enabled: boolean;
  rounding_values: number[];
  settings_type: 'rounding';
};

export type RoomOffsetHierarchy = {
  children: Record<string, RoomOffsetHierarchy>;
  maximum_offset?: { type: string; value: number };
  offset: { type: string; value: number };
};

export type RoomOffsetHierarchySettings = {
  flattened_hierarchy?: {
    offset: { type: string; value: number };
    pms_room_id: string;
  }[];
  hierarchy: Record<string, RoomOffsetHierarchy>;
  settings_type: 'room_offset_hierarchy';
};

export type CompsetPositioningLevelValues =
  | (typeof COMPETITORS_IMPACTS_STRATEGY_LEVELS)[keyof typeof COMPETITORS_IMPACTS_STRATEGY_LEVELS]
  | typeof COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY; // FE only state to mark undropped competitors during initial assignment

export type CompsetPositioningDayOfWeekSettings = {
  [key: string]: CompsetPositioningLevelValues;
};

export type CompsetPositioningSettings = {
  day_of_week: {
    [key: number]: CompsetPositioningDayOfWeekSettings;
  };
  settings_type: 'compset_positioning';
};

export type RollingReferenceRatesSettings = {
  day_of_week: {
    [dayOfWeek: number]: {
      [roomId: string]: number | null;
    };
  };
  settings_type: 'rolling_reference_rates';
};

export type DynamicRateDiscount = {
  type: 'absolute' | 'relative';
  value: number;
};

export type DynamicRateDiscountRangeSettings = {
  rate_discounts: {
    [rateCode: string]: {
      maximum: DynamicRateDiscount | null;
      minimum: DynamicRateDiscount | null;
      user_defined_rate_code_name?: string;
    };
  };
  settings_type: 'dynamic_rate_discount_range';
};

export default class RecommendationStrategy extends Model {
  [Type] = 'recommendation-strategy' as const;

  @hasMany('conditional-strategy', { async: false, inverse: null })
  declare conditionalStrategies: HasMany<ConditionalStrategy>;

  @attr() declare conditions: StrategyCondition | null;
  @attr('json') declare conditionSummary: LeadtimeConditionSummary | null;
  @attr('dayjs') declare createdAt: DayjsTransformReturn;
  @attr('string') declare name: string;
  @attr('hoteldate') declare seasonEndDt: HoteldateTransformReturn;
  @attr('hoteldate') declare seasonStartDt: HoteldateTransformReturn;

  @attr() declare settings:
    | PriceRangeSettings
    | RoundingSettings
    | CompsetPositioningSettings
    | AllowedRecommendationTypeSettings
    | MinimumPriceDifferenceSettings
    | RoomOffsetHierarchySettings
    | RollingReferenceRatesSettings
    | DynamicRateDiscountRangeSettings;

  @attr('string') declare settingsType: (typeof this.settings)['settings_type'];

  @attr('number') declare subscriptionId: number;
  @attr('dayjs') declare updatedAt: DayjsTransformReturn;
  @service declare access: Services['access'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetPositioningCompetitorsLeftToAssignInStartingState() {
    if (
      this.settings.settings_type === 'compset_positioning' &&
      this.isCompsetPositioningStartingState
    ) {
      const defaultImpactedDays: number[] = [1, 2, 3, 4, 5, 6, 7];

      const competitorImpactSettings = this.settings.day_of_week;

      const dowSettings = competitorImpactSettings[
        defaultImpactedDays[0] as number
      ] as CompsetPositioningDayOfWeekSettings;

      const competitorIds = Object.keys(dowSettings).filter((competitorId) => {
        return (
          dowSettings[parseInt(competitorId, 10)] ===
          COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY
        );
      });

      return competitorIds;
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get doesCompsetPositioningHaveInvalidSelection() {
    if (this.settings.settings_type === 'compset_positioning') {
      const competitorImpactSettings = this.settings.day_of_week;

      const daysWithInvalidSelection = Object.keys(
        competitorImpactSettings,
      ).filter((dow) => {
        const dowSettings = competitorImpactSettings[
          parseInt(dow, 10)
        ] as CompsetPositioningDayOfWeekSettings;
        return Object.values(dowSettings).every(
          (value) => value === COMPETITORS_IMPACTS_STRATEGY_LEVELS.IRRELEVANT,
        );
      });

      return {
        hasInvalidSelection: daysWithInvalidSelection.length > 0,
        daysWithInvalidSelection,
      };
    }

    return {
      hasInvalidSelection: false,
      daysWithInvalidSelection: [],
    };
  }

  @cached
  get dynamicRateDiscountRangesWithError(): Record<
    DynamicRateDiscountRangeErrorKind,
    string[]
  > {
    const { settings } = this;

    if (settings.settings_type !== 'dynamic_rate_discount_range') {
      return {
        minGreaterThanMax: [],
        minOrMaxMissing: [],
        minOrMaxPartiallySet: [],
      };
    }

    // A parent strategy (default or seasonal) requires both bounds on every
    // rate; a leadtime-band child does not.
    return getRatesWithDiscountRangeError(
      settings,
      !this.isConditionalStrategy,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get errorMessageForInvalidStrategy() {
    if (this.settingsType === 'price_range') {
      return this.intl.t(
        'pricing_manager.recommendations.min_higher_than_max_error',
      );
    }

    if (this.settingsType === 'dynamic_rate_discount_range') {
      // The card shows one message for the whole group, so the worst kind is
      // taken across the parent and its bands (a partial-fill only ever occurs
      // on a band).
      const strategies = [this, ...this.conditionalStrategies];
      const kind = DYNAMIC_RATE_DISCOUNT_RANGE_ERROR_KIND_PRECEDENCE.find(
        (errorKind) =>
          strategies.some(
            (strategy) =>
              strategy.dynamicRateDiscountRangesWithError[errorKind].length > 0,
          ),
      );

      if (kind === 'minOrMaxMissing') {
        return this.intl.t('general.required');
      }

      if (kind === 'minOrMaxPartiallySet') {
        return this.intl.t(
          'pricing_manager.recommendations.dynamic_rate_ranges.partial_fill_error',
        );
      }

      return this.intl.t(
        'pricing_manager.recommendations.dynamic_rate_ranges.min_higher_than_max_error',
      );
    }
  }

  @cached
  get invalidOffsetRoomCodes(): string[] {
    const { settings } = this;

    if (
      settings.settings_type !== 'room_offset_hierarchy' ||
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_CASCADING_MINMAX_ROOM_OFFSETS,
      )
    ) {
      return [];
    }

    return getInvalidOffsetRoomCodes(settings.hierarchy);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCompsetPositioningStartingState() {
    if (
      this.settingsType === 'compset_positioning' &&
      this.isDefaultStrategy &&
      this.createdAt &&
      this.updatedAt
    ) {
      // Check if difference between createdAt and updatedAt is less than 1 second
      // Currently this is the only way to tell if a default strategy is defined by BE or by the user
      // This is relevant for a custom start state on the Recommendations compset positioning settings page
      return Math.abs(this.createdAt.diff(this.updatedAt, 'seconds')) < 1;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCompsetPositioningStartingStateInvalid() {
    return (
      this.settingsType === 'compset_positioning' &&
      this.isCompsetPositioningStartingState &&
      this.compsetPositioningCompetitorsLeftToAssignInStartingState.length > 0
    );
  }

  /* Overridden to true on ConditionalStrategy. Distinguishes a top-level
   * strategy (default or seasonal) from one of its leadtime-band children. */
  get isConditionalStrategy(): boolean {
    return false;
  }

  /* Default strategy is the one without start and end date */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDefaultStrategy() {
    return !this.seasonStartDt && !this.seasonEndDt && this.name === 'default';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSeasonalStrategy() {
    return !this.isDefaultStrategy;
  }

  get isStrategyInvalid(): boolean {
    if (this.settings.settings_type === 'dynamic_rate_discount_range') {
      return [this, ...this.conditionalStrategies].some((strategy) =>
        Object.values(strategy.dynamicRateDiscountRangesWithError).some(
          (rates) => rates.length > 0,
        ),
      );
    }

    if (this.settings.settings_type === 'room_offset_hierarchy') {
      return this.invalidOffsetRoomCodes.length > 0;
    }

    if (this.settings.settings_type !== 'price_range') {
      return (
        this.isCompsetPositioningStartingStateInvalid ||
        this.doesCompsetPositioningHaveInvalidSelection.hasInvalidSelection
      );
    }

    if (this.access.hasPricingAssistantOpenPricingFeature) {
      // Multi-Room price range
      return this.multiRoomPriceRangeStrategyDayOfWeekWithErrors.length > 0;
    }

    // Single room price range
    return this.priceRangeStrategyDayOfWeekWithErrors.length > 0;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get multiRoomPriceRangeStrategyDayOfWeekWithErrors() {
    const { settings } = this;

    if (
      settings.settings_type !== 'price_range' ||
      !this.access.hasPricingAssistantOpenPricingFeature
    ) {
      return [];
    }

    const daysAndRoomsWithPriceRangeError =
      getMultiRoomDaysWithPriceRangeError(settings);

    return daysAndRoomsWithPriceRangeError;
  }

  /*
  Each table cell checks if it belongs to a day of week with errors. This causes this getter to be called multiple times.
  To avoid recalculating the same value, we cache it.
  */
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceRangeStrategyDayOfWeekWithErrors() {
    const { conditionalStrategies, settings } = this;

    if (
      settings.settings_type !== 'price_range' ||
      this.access.hasPricingAssistantOpenPricingFeature
    ) {
      return [];
    }

    const daysWithPriceRangeError = getDaysWithPriceRangeError(settings, false);

    if (
      conditionalStrategies.length === 0 ||
      !conditionalStrategies[0]!.settings
    ) {
      return daysWithPriceRangeError;
    }

    const conditionalSettings = conditionalStrategies[0]!.settings;

    const conditionalDaysWithPriceRangeError =
      conditionalSettings.settings_type === 'price_range'
        ? getDaysWithPriceRangeError(conditionalSettings, true)
        : [];

    return [...daysWithPriceRangeError, ...conditionalDaysWithPriceRangeError];
  }
}
